import React, { useState } from 'react';
import { X, Image as ImageIcon, Video as VideoIcon, Calendar, Link as LinkIcon } from 'lucide-react';

interface SocialMediaPostModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SocialMediaPostModal({ isOpen, onClose }: SocialMediaPostModalProps) {
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [content, setContent] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement post creation
    console.log({
      platforms: selectedPlatforms,
      content,
      scheduledDate,
      scheduledTime,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Nouvelle publication</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sélection des plateformes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plateformes
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="instagram"
                    checked={selectedPlatforms.includes('instagram')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPlatforms([...selectedPlatforms, 'instagram']);
                      } else {
                        setSelectedPlatforms(selectedPlatforms.filter(p => p !== 'instagram'));
                      }
                    }}
                    className="mr-2"
                  />
                  Instagram
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="facebook"
                    checked={selectedPlatforms.includes('facebook')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPlatforms([...selectedPlatforms, 'facebook']);
                      } else {
                        setSelectedPlatforms(selectedPlatforms.filter(p => p !== 'facebook'));
                      }
                    }}
                    className="mr-2"
                  />
                  Facebook
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    value="youtube"
                    checked={selectedPlatforms.includes('youtube')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPlatforms([...selectedPlatforms, 'youtube']);
                      } else {
                        setSelectedPlatforms(selectedPlatforms.filter(p => p !== 'youtube'));
                      }
                    }}
                    className="mr-2"
                  />
                  YouTube
                </label>
              </div>
            </div>

            {/* Contenu */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contenu
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Que souhaitez-vous partager ?"
              />
            </div>

            {/* Médias */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Médias
              </label>
              <div className="flex gap-4">
                <button
                  type="button"
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <ImageIcon className="w-5 h-5 mr-2" />
                  Ajouter une image
                </button>
                <button
                  type="button"
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <VideoIcon className="w-5 h-5 mr-2" />
                  Ajouter une vidéo
                </button>
                <button
                  type="button"
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <LinkIcon className="w-5 h-5 mr-2" />
                  Ajouter un lien
                </button>
              </div>
            </div>

            {/* Programmation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Programmation
              </label>
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md"
                />
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            {/* Aperçu */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-2">Aperçu</h3>
              <div className="bg-white p-4 rounded border">
                <div className="flex items-center gap-2 mb-2">
                  {selectedPlatforms.map((platform) => (
                    <span
                      key={platform}
                      className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                    >
                      {platform}
                    </span>
                  ))}
                </div>
                <p className="text-gray-900 whitespace-pre-wrap">{content}</p>
                {(scheduledDate || scheduledTime) && (
                  <div className="mt-2 text-sm text-gray-500 flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    Programmé pour le {scheduledDate} à {scheduledTime}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                Publier
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}